import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';
import { useStorage } from 'vue3-storage';

import MainLayout from '@/layouts/MainLayout/MainLayout.vue';
import AuthLayout from '@/layouts/AuthLayout/AuthLayout.vue';
import Landing from '@/views/landing/Landing.vue';
import Login from '@/views/auth/login/Login.vue';
import Register from '@/views/auth/register/Register.vue';

// handbooks
import HandbookUsers from '@/views/handbooks/HandbookUsers/HandbookUsers.vue';
import HandbookDeliveryPrices from '@/views/handbooks/HandbookDeliveryPrices/HandbookDeliveryPrices.vue';
import HandbookCities from '@/views/handbooks/HandbookCities/HandbookCities.vue';
import HandbookComplectations from '@/views/handbooks/HandbookComplectations/HandbookComplectations.vue';
import HandbookDealers from '@/views/handbooks/HandbookDealers/HandbookDealers.vue';
import HandbookHoldings from '@/views/handbooks/HandbookHoldings/HandbookHoldings.vue';
import HandbookModels from '@/views/handbooks/HandbookModels/HandbookModels.vue';

// complectations
import AddComplectation from '@/views/handbooks/HandbookComplectations/AddComplectation/AddComplectation.vue';
import EditComplectation from '@/views/handbooks/HandbookComplectations/EditComplectation/EditComplectation.vue';

// cars
import CarsList from '@/views/cars/CarsList/CarsList.vue';
import ViewCar from '@/views/cars/ViewCar/ViewCar.vue';
import AddCar from '@/views/cars/AddCar/AddCar.vue';
import EditCar from '@/views/cars/EditCar/EditCar.vue';

// errors
import Error403 from '@/views/errors/403/403.vue';
import Error404 from '@/views/errors/404/404.vue';

// auctions
import AuctionsList from '@/views/auctions/AuctionsList/AuctionsList.vue';
import ViewAuction from '@/views/auctions/ViewAuction/ViewAuction.vue';
import CreateAuction from '@/views/auctions/CreateAuction/CreateAuction.vue';
import EditAuction from '@/views/auctions/EditAuction/EditAuction.vue';

// bets
import BetsList from '@/views/bets/BetsList/BetsList.vue';

// dealers
import AddDealer from '@/views/handbooks/HandbookDealers/AddDealer/AddDealer.vue';
import EditDealer from '@/views/handbooks/HandbookDealers/EditDealer/EditDealer.vue';

// holdings
import AddHolding from '@/views/handbooks/HandbookHoldings/AddHolding/AddHolding.vue';
import EditHolding from '@/views/handbooks/HandbookHoldings/EditHolding/EditHolding.vue';

// delivery-prices
import EditDeliveryPrice from '@/views/handbooks/HandbookDeliveryPrices/EditDeliveryPrice/EditDeliveryPrice.vue';

// cities
import AddCity from '@/views/handbooks/HandbookCities/AddCity/AddCity.vue';
import EditCity from '@/views/handbooks/HandbookCities/EditCity/EditCity.vue';

// models
import AddModel from '@/views/handbooks/HandbookModels/AddModel/AddModel.vue';
import EditModel from '@/views/handbooks/HandbookModels/EditModel/EditModel.vue';

// profile
import Profile from '@/views/profile/Profile/Profile.vue';
import EditRequisites from '@/views/profile/EditRequisites/EditRequisites.vue';

// reports
import BidReportList from '@/views/reports/BidReportList/BidReportList.vue';
import BidReportDetail from '@/views/reports/BidReportDetail/BidReportDetail.vue';
import ReportAccounting from '@/views/reports/ReportAccounting/ReportAccounting.vue';
import ReportPlacementSale from '@/views/reports/ReportPlacementSale/ReportPlacementSale.vue';
import ReportAuctions from '@/views/reports/ReportAuctions/ReportAuctions.vue';

// documents
import DocumentsList from '@/views/documents/DocumentsList/DocumentsList.vue';

// users
import AddUser from '@/views/handbooks/HandbookUsers/AddUser/AddUser.vue';
import EditUser from '@/views/handbooks/HandbookUsers/EditUser/EditUser.vue';

// pages
import ViewRules from '@/views/pages/rules/ViewRules/ViewRules.vue';
import ViewLegal from '@/views/pages/legal/ViewLegal/ViewLegal.vue';

import { injectStrict } from '@/injection/injectStrict.ts';
import { ModalKey, PagePropertiesKey } from '@/injection/keys.ts';
import usePermission from '@/repositories/auth/usePermission.ts';
import { ModalPluginType } from '@/plugins/modalPlugin.ts';

/*
import DeliveryRates from '@/views/DeliveryRates.vue';
import Cities from '@/views/Cities.vue';
import Models from '@/views/Models.vue';
import Configurations from '@/views/Configurations.vue';
*/

function isAdmin(): boolean {
    const { isAdmin } = usePermission();

    return isAdmin();
}

function isDealer(): boolean {
    const { isAdmin } = usePermission();

    return !isAdmin();
}

function isCertifiedDealer(): boolean {
    const { isCertifiedDealer } = usePermission();

    return isCertifiedDealer();
}

const $storage = useStorage();

const routes: Array<RouteRecordRaw> = [
    {
        path: '/',
        name: 'Landing',
        component: Landing,
    },
    {
        path: '/auth/login',
        name: 'Login',
        component: AuthLayout,
        children: [
            {
                path: '/auth/login',
                name: 'Login',
                component: Login,
            },
        ],
    },
    {
        path: '/auth/register',
        name: 'Register',
        component: AuthLayout,
        children: [
            {
                path: '/auth/register',
                name: 'Register',
                component: Register,
            },
        ],
    },
    {
        path: '/cars',
        name: 'CarsList',
        component: MainLayout,
        children: [
            {
                path: '/cars',
                name: 'CarsList',
                component: CarsList,
            },
            {
                path: '/cars/add',
                name: 'AddCar',
                children: [
                    {
                        path: '/cars/add',
                        name: 'AddCar',
                        component: AddCar,
                        meta: {
                            checkAccessFunctions: [isCertifiedDealer],
                        },
                    },
                ],
            },
            {
                path: '/cars/edit/:car_id(\\d+)',
                name: 'EditCar',
                children: [
                    {
                        path: '/cars/edit/:car_id(\\d+)',
                        name: 'EditCar',
                        component: EditCar,
                        meta: {
                            checkAccessFunctions: [isAdmin, isCertifiedDealer],
                        },
                    },
                ],
            },
            {
                path: '/cars/:car_id(\\d+)',
                name: 'ViewCar',
                children: [
                    {
                        path: '/cars/:car_id(\\d+)',
                        name: 'ViewCar',
                        component: ViewCar,
                    },
                ],
            },
        ],
    },
    {
        path: '/403',
        name: 'Error403',
        component: MainLayout,
        children: [
            {
                path: '/403',
                name: 'Error403',
                component: Error403,
            },
        ],
    },
    {
        path: '/404',
        name: 'Error404',
        component: MainLayout,
        children: [
            {
                path: '/:catchAll(.*)',
                name: 'Error404',
                component: Error404,
            },
        ],
    },
    {
        path: '/bets',
        name: 'BetsList',
        component: MainLayout,
        children: [
            {
                path: '/bets',
                name: 'BetsList',
                component: BetsList,
                meta: {
                    checkAccessFunctions: [isDealer],
                },
            },
        ],
    },
    {
        path: '/auctions',
        name: 'AuctionsList',
        component: MainLayout,
        children: [
            {
                path: '/auctions',
                name: 'AuctionsList',
                component: AuctionsList,
            },
            {
                path: '/auctions/create',
                name: 'CreateAuction',
                component: CreateAuction,
                meta: {
                    checkAccessFunctions: [isAdmin],
                },
            },
            {
                path: '/auctions/edit/:auction_id(\\d+)',
                name: 'EditAuction',
                component: EditAuction,
                meta: {
                    checkAccessFunctions: [isAdmin],
                },
            },
            {
                path: '/auctions/:auction_id(\\d+)',
                name: 'ViewAuction',
                component: ViewAuction,
            },
        ],
    },
    // handbooks
    {
        path: '/handbooks',
        name: 'Handbooks',
        component: MainLayout,
        children: [
            // users
            {
                path: 'users',
                name: 'HandbookUsers',
                component: HandbookUsers,
            },
            {
                path: 'users/add',
                name: 'AddUser',
                component: AddUser,
            },
            {
                path: 'users/edit/:user_id(\\d+)',
                name: 'EditUser',
                component: EditUser,
            },
            // delivery-prices
            {
                path: 'delivery-prices',
                name: 'HandbookDeliveryPrices',
                component: HandbookDeliveryPrices,
            },
            {
                path: 'delivery-prices/edit/:delivery_price_id(\\d+)',
                name: 'EditDeliveryPrice',
                component: EditDeliveryPrice,
            },
            // cities
            {
                path: 'cities',
                name: 'HandbookCities',
                component: HandbookCities,
            },
            {
                path: 'cities/add',
                name: 'AddCity',
                component: AddCity,
            },
            {
                path: 'cities/edit/:city_id(\\d+)',
                name: 'EditCity',
                component: EditCity,
            },
            // complectations
            {
                path: 'complectations',
                name: 'HandbookComplectations',
                component: HandbookComplectations,
            },
            {
                path: 'complectations/edit/:complectation_id(\\d+)',
                name: 'EditComplectation',
                component: EditComplectation,
            },
            {
                path: 'complectations/add',
                name: 'AddComplectation',
                component: AddComplectation,
            },
            // dealers
            {
                path: 'dealers',
                name: 'HandbookDealers',
                component: HandbookDealers,
            },
            {
                path: 'dealers/add',
                name: 'AddDealer',
                component: AddDealer,
            },
            {
                path: 'dealers/edit/:dealer_id(\\d+)',
                name: 'EditDealer',
                component: EditDealer,
            },
            // holdings
            {
                path: 'holdings',
                name: 'HandbookHoldings',
                component: HandbookHoldings,
            },
            {
                path: 'holdings/add',
                name: 'AddHolding',
                component: AddHolding,
            },
            {
                path: 'holdings/edit/:holding_id(\\d+)',
                name: 'EditHolding',
                component: EditHolding,
            },

            // models
            {
                path: 'models/:brand?',
                name: 'HandbookModels',
                component: HandbookModels,
                meta: {
                    title: 'Модели',
                },
            },
            {
                path: 'models/add',
                name: 'AddModel',
                component: AddModel,
            },
            {
                path: 'models/edit/:model_id',
                name: 'EditModel',
                component: EditModel,
            },
        ],
        meta: {
            checkAccessFunctions: [isAdmin],
        },
    },
    // profile
    {
        path: '/profile',
        component: MainLayout,
        children: [
            {
                path: '/profile/:tab?',
                name: 'Profile',
                component: Profile,
                meta: {
                    checkAccessFunctions: [isDealer],
                },
            },
        ],
    },
    {
        path: '/profile/requisites/edit',
        component: MainLayout,
        children: [
            {
                path: '/profile/requisites/edit',
                name: 'EditRequisites',
                component: EditRequisites,
                meta: {
                    checkAccessFunctions: [isDealer],
                },
            },
        ],
    },
    // reports
    {
        path: '/reports',
        name: 'Reports',
        component: MainLayout,
        children: [
            {
                path: '/reports/bid-reports',
                name: 'BidReportList',
                component: BidReportList,
            },
            {
                path: '/reports/bid-reports/:auction_id(\\d+)',
                name: 'BidReportDetail',
                component: BidReportDetail,
            },
            {
                path: '/reports/accounting',
                name: 'ReportAccounting',
                component: ReportAccounting,
            },
            {
                path: '/reports/placement-sale',
                name: 'ReportPlacementSale',
                component: ReportPlacementSale,
            },
            {
                path: '/reports/auctions',
                name: 'ReportAuctions',
                component: ReportAuctions,
            },
        ],
        meta: {
            checkAccessFunctions: [isAdmin],
        },
    },

    // documents
    {
        path: '/documents',
        component: MainLayout,
        children: [
            {
                path: '/documents',
                name: 'DocumentsList',
                component: DocumentsList,
            },
        ],
    },

    // pages
    {
        path: '/rules',
        name: 'ViewRules',
        component: MainLayout,
        children: [
            {
                path: '/rules',
                name: 'ViewRules',
                component: ViewRules,
            },
        ],
    },
    {
        path: '/legal',
        name: 'ViewLegal',
        component: MainLayout,
        children: [
            {
                path: '/legal',
                name: 'ViewLegal',
                component: ViewLegal,
            },
        ],
    },
];

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL || '/'),
    routes,
    scrollBehavior(_to, _from, savedPosition) {
        if (savedPosition) {
            return savedPosition;
        }

        return { top: 0, behavior: 'smooth' };
    },
});

router.beforeEach((to, _from, next): void => {
    const publicPages = ['/auth/login', '/auth/register', '/', '/rules', '/legal'];
    const authRequired = !publicPages.includes(to.path);
    const loggedIn = $storage.getStorageSync('token');

    if (authRequired && !loggedIn) {
        return next({ name: 'Login' });
    }

    if (
        to.matched.some(record => record.name === 'Login' || record.name === 'Register')
        && loggedIn
    ) {
        return next({ name: 'Landing' });
    }

    let checkAccess;
    if (Array.isArray(to.meta.checkAccessFunctions) && to.meta.checkAccessFunctions.length > 0) {
        checkAccess = to.meta.checkAccessFunctions.some((checkAccessFunction): boolean => {
            return !!checkAccessFunction();
        });
    }
    else {
        checkAccess = true;
    }

    const $PageProperties = injectStrict(PagePropertiesKey);
    $PageProperties.value.accessDenied = !checkAccess;

    next();
});

router.beforeEach((to, _from, next): void => {
    const $modal = injectStrict<ModalPluginType>(ModalKey);

    $modal.close();

    next();
});

export default router;
